import type { Suggestion } from '~/components/SearchSuggest';
import type { MaybeRefOrGetter, Ref, ShallowRef } from 'vue';

import { nextTick, toValue } from 'vue';

export interface UseSearchSuggestUncommittedDismissOptions {
	parentSearchTerm: MaybeRefOrGetter<string | undefined>;
	onClearParentSearchTerm: () => void;
	searchTerm: Ref<string>;
	modelValue: Ref<Suggestion | undefined>;
	isUpdatingFromWatch: Ref<boolean>;
	hasSelectedValue: ShallowRef<boolean>;
	backupSearchTerm: Ref<string>;
	handleApplySearchTerm: () => void;
}

/**
 * When the user closes search UI without confirming a list item: discard draft
 * text or re-sync the label from the parent (URL / store).
 *
 * **Modal com rótulo do pai:** via `useSearchSuggestDialogDismissRestore`.
 * **Dropdown desktop** (`SearchSuggestField` / `Suggest.vue`): `closeDropdown()`.
 * Mesma regra de produto nos dois fluxos.
 *
 * **Not** used by the SRP listing modal (`SuggestDialog`); that flow uses
 * `closeDialog` / `syncDialogOpen` only.
 */
export function useSearchSuggestUncommittedDismiss(
	options: UseSearchSuggestUncommittedDismissOptions
) {
	function parentLabel(): string | undefined {
		const v = toValue(options.parentSearchTerm);
		return v?.trim() ? v : undefined;
	}

	function hasUncommittedInput(): boolean {
		const currentTrimmed = options.searchTerm.value.trim();
		const parentTrimmed = toValue(options.parentSearchTerm)?.trim() ?? '';
		// Uncommitted input exists only if the current text is not empty
		// and is different from the parent's state.
		return currentTrimmed.length > 0 && currentTrimmed !== parentTrimmed;
	}

	function reapplySelectionLabelFromParent(): void {
		options.isUpdatingFromWatch.value = true;
		options.searchTerm.value = '';
		nextTick(() => {
			options.handleApplySearchTerm();
			options.hasSelectedValue.value = true;
		});
	}

	function clearInputWithoutParentSelection(): void {
		options.isUpdatingFromWatch.value = true;
		options.searchTerm.value = '';
		options.onClearParentSearchTerm();
		options.modelValue.value = undefined;
		options.hasSelectedValue.value = false;
	}

	function discardUncommittedInput(): void {
		options.backupSearchTerm.value = '';
		if (parentLabel()) {
			reapplySelectionLabelFromParent();
		} else {
			clearInputWithoutParentSelection();
		}
	}

	function restoreParentLabelIfDraftFieldWasCleared(): void {
		if (!parentLabel()) return;
		if (!options.searchTerm.value.trim() || !options.modelValue.value) {
			options.isUpdatingFromWatch.value = true;
			options.handleApplySearchTerm();
			options.hasSelectedValue.value = true;
		}
	}

	/** Run when closing dropdown/modal without a confirmed list selection. */
	function applyOnCloseWithoutConfirmedSelection(): void {
		if (hasUncommittedInput()) {
			discardUncommittedInput();
			return;
		}
		restoreParentLabelIfDraftFieldWasCleared();
	}

	return {
		hasUncommittedInput,
		discardUncommittedInput,
		reapplySelectionLabelFromParent,
		clearInputWithoutParentSelection,
		restoreParentLabelIfDraftFieldWasCleared,
		applyOnCloseWithoutConfirmedSelection
	};
}
